import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Pipeline } from '../../store/pipelineStore';

interface Props {
  startupId: string;
  pipeline: Pipeline | null;
}

export function BetaRecruitmentPanel({ startupId, pipeline }: Props) {
  const [whyOpen, setWhyOpen] = useState(false);

  const raw = pipeline?.positioning?.aiOutput?.['earlyAdopterPersona'];
  const earlyAdopterPersona: string | null = !raw ? null
    : typeof raw === 'string' ? raw
    : typeof raw === 'object'
      ? [
          (raw as Record<string, unknown>).name,
          (raw as Record<string, unknown>).job,
          (raw as Record<string, unknown>).quote,
        ].filter(Boolean).join(' · ') || null
      : null;

  const rawChecklist = pipeline?.validationPath?.aiOutput?.['checklist'];
  const checklistItems: string[] = Array.isArray(rawChecklist)
    ? (rawChecklist as unknown[]).slice(0, 3).map(String)
    : [];

  return (
    <div className="card" style={{ padding: '20px 16px 20px' }}>
      {/* Icon + Heading */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 }}>
        <div style={{
          width: 48, height: 48, borderRadius: 12, flexShrink: 0,
          background: 'rgba(124,111,247,0.15)', border: '1px solid rgba(124,111,247,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22,
        }}>
        </div>
        <div>
          <h3 style={{ fontSize: 17, fontWeight: 700, color: '#fff', marginBottom: 2 }}>
            Get your first 10 beta users
          </h3>
          <p style={{ fontSize: 13, color: '#6b7280' }}>
            Real users = real feedback = fundable traction.
          </p>
        </div>
      </div>

      {/* Early adopter persona */}
      {earlyAdopterPersona ? (
        <div style={{
          background: 'rgba(124,111,247,0.07)', border: '1px solid rgba(124,111,247,0.18)',
          borderRadius: 10, padding: '10px 14px', marginBottom: 16,
        }}>
          <p style={{ fontSize: 11, color: '#7c6ff7', fontWeight: 600, marginBottom: 3, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Your early adopter persona
          </p>
          <p style={{ fontSize: 13, color: '#d1d5db', lineHeight: 1.5 }}>{earlyAdopterPersona}</p>
        </div>
      ) : (
        <div style={{
          background: '#13152a', border: '1px solid #1e2140',
          borderRadius: 10, padding: '10px 14px', marginBottom: 16,
        }}>
          <p style={{ fontSize: 13, color: '#4b5563', lineHeight: 1.5 }}>
            Complete your positioning step in the pipeline to see your ideal early adopter persona here.
          </p>
        </div>
      )}

      {/* CTA button */}
      <Link
        to={`/startups/${startupId}/beta`}
        style={{
          display: 'block', textAlign: 'center', textDecoration: 'none',
          background: 'linear-gradient(135deg, #7c6ff7, #00d4ff)',
          color: '#fff', fontWeight: 700, fontSize: 15,
          borderRadius: 10, padding: '12px 0',
          marginBottom: 16, transition: 'opacity 0.15s',
        }}
        onMouseEnter={e => (e.currentTarget.style.opacity = '0.88')}
        onMouseLeave={e => (e.currentTarget.style.opacity = '1')}
      >
        → Go to Beta Testing
      </Link>

      {/* "Why beta test now?" disclosure */}
      <button
        onClick={() => setWhyOpen(v => !v)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          display: 'flex', alignItems: 'center', gap: 6,
          color: '#6b7280', fontSize: 13,
        }}
      >
        <span style={{ display: 'inline-block', transition: 'transform 0.2s', transform: whyOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▶</span>
        Why beta test now?
      </button>

      {whyOpen && (
        <div style={{ marginTop: 10, paddingLeft: 18 }}>
          {checklistItems.length > 0 ? (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {checklistItems.map((item, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: '#9ca3af', lineHeight: 1.4 }}>
                  <span style={{ color: '#7c6ff7', flexShrink: 0, marginTop: 1 }}>•</span>
                  {item}
                </li>
              ))}
            </ul>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[
                'Beta users prove demand before you write more code.',
                'Investors want to see real users, not just market research.',
                'Feedback now saves months of building the wrong thing.',
              ].map((item, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: '#9ca3af', lineHeight: 1.4 }}>
                  <span style={{ color: '#7c6ff7', flexShrink: 0, marginTop: 1 }}>•</span>
                  {item}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
